/**
 * Surface pieces — the voxel field's geometry, enumerated as SURFACES rather than sampled
 * along directions.
 *
 * WHY THIS EXISTS. The original engine collided the capsule against the RENDERED TRIANGLE MESH
 * through three-mesh-bvh: every triangle near the capsule contributed a closest-point push, all
 * of them jointly. Every field-based mechanism that replaced it looked along DIRECTIONS instead
 * (vertical columns, horizontal whiskers, roll rays). A direction sampler has to pick one
 * surface where two meet, picking is a discrete decision, and a discrete decision that flips as
 * the player walks is exactly what "gritty" and "step-like" describe. Measured on a 0.25 m
 * riser, the column model produced 0 0 0 46 52 55 24 0 19 54 mm of lift per tick — bursts and
 * stalls, six direction reversals per step.
 *
 * The fix is not a better sampler. It is to give the solver back the surfaces. One MIXED voxel
 * cell contributes one piece:
 *
 *   point  — the SurfaceNets vertex: the mean of this cell's edge crossings. This is the very
 *            vertex the mesher emits (client/src/game/voxel/SurfaceNet.ts), so collision
 *            geometry IS rendered geometry, which is what the BVH was feeding the old solver.
 *   normal — the analytic trilinear gradient at that vertex, negated (the field rises INTO
 *            solid), from the same 8 corner weights.
 *   bounds — the cell box. A triangle is bounded; without this a distant cell's infinite plane
 *            would phantom-contact the capsule.
 *
 * CONTINUITY, which is the whole point, holds by construction: a piece's contribution is
 * max(0, reach - distance), distance is continuous in the capsule pose (projection onto a plane
 * and a box clamp are both continuous), and a cell entering the collection window does so at
 * distance >= radius + margin > reach — that is, contributing exactly zero. So the number of
 * pieces can change without stepping the result. No argmax anywhere. An edge presents BOTH of
 * its surfaces at once instead of forcing a choice between them.
 *
 * Coverage is structural too: every cell in the window is enumerated, so there is no analogue of
 * the whisker rings' spacing rule, where an obstacle that fit between two rings was invisible to
 * the entire horizontal phase.
 *
 * Cost: one corner-weight lattice per solve (~360 reads for the player capsule) and then pure
 * arithmetic. The whisker/column model spent thousands of corner reads per tick, because every
 * `sampleSolid` inside every ray bisection is 8 of them.
 */
import { VOXEL_SCALE } from '../voxel/constants.js';
import { INV_VOXEL_SCALE } from './voxelField.js';
/**
 * Cell-window clamp. The player capsule's window is 5 x 9 x 5 cells; this bounds the work for
 * any hull without a silent truncation in practice (exceeding it would need a hull ~2x larger
 * in every axis).
 */
const MAX_CELLS_X = 8;
const MAX_CELLS_Y = 16;
const MAX_CELLS_Z = 8;
const MAX_CORNERS = (MAX_CELLS_X + 1) * (MAX_CELLS_Y + 1) * (MAX_CELLS_Z + 1);
/** One piece per mixed cell; a full window of mixed cells still fits. */
const MAX_PIECES = MAX_CELLS_X * MAX_CELLS_Y * MAX_CELLS_Z;
/**
 * Cube corner convention, shared with the mesher: bit 0 = +x, bit 1 = +y, bit 2 = +z.
 * CUBE_EDGES holds the 12 edges as corner-index pairs, built exactly as SurfaceNet.ts does.
 */
const CUBE_EDGES = new Int32Array(24);
{
    let k = 0;
    for (let i = 0; i < 8; i++) {
        for (let j = 1; j <= 4; j <<= 1) {
            const p = i ^ j;
            if (i <= p) {
                CUBE_EDGES[k++] = i;
                CUBE_EDGES[k++] = p;
            }
        }
    }
}
export function makePieceSet() {
    return {
        count: 0,
        vx: new Float64Array(MAX_PIECES),
        vy: new Float64Array(MAX_PIECES),
        vz: new Float64Array(MAX_PIECES),
        nx: new Float64Array(MAX_PIECES),
        ny: new Float64Array(MAX_PIECES),
        nz: new Float64Array(MAX_PIECES),
        loX: new Float64Array(MAX_PIECES),
        loY: new Float64Array(MAX_PIECES),
        loZ: new Float64Array(MAX_PIECES),
        hiX: new Float64Array(MAX_PIECES),
        hiY: new Float64Array(MAX_PIECES),
        hiZ: new Float64Array(MAX_PIECES),
    };
}
/** Corner-weight lattice for the current window (module scratch; engine is single-threaded). */
const lattice = new Float64Array(MAX_CORNERS);
/** This cell's 8 corner weights, indexed by the bit convention above. */
const cw = new Float64Array(8);
/**
 * Enumerate every mixed cell overlapping the world-space AABB and write one piece each.
 *
 * UNKNOWN chunks read as solid via `cornerSolidW`, the same conservative rule the rest of the
 * engine uses — but the caller's unknown-space freeze is what makes that irrelevant in practice,
 * and its region margin must cover this AABB.
 */
export function collectPieces(field, minX, minY, minZ, maxX, maxY, maxZ, out) {
    out.count = 0;
    const g0x = Math.floor(minX * INV_VOXEL_SCALE);
    const g0y = Math.floor(minY * INV_VOXEL_SCALE);
    const g0z = Math.floor(minZ * INV_VOXEL_SCALE);
    let cellsX = Math.floor(maxX * INV_VOXEL_SCALE) - g0x + 1;
    let cellsY = Math.floor(maxY * INV_VOXEL_SCALE) - g0y + 1;
    let cellsZ = Math.floor(maxZ * INV_VOXEL_SCALE) - g0z + 1;
    if (cellsX < 1 || cellsY < 1 || cellsZ < 1)
        return;
    if (cellsX > MAX_CELLS_X)
        cellsX = MAX_CELLS_X;
    if (cellsY > MAX_CELLS_Y)
        cellsY = MAX_CELLS_Y;
    if (cellsZ > MAX_CELLS_Z)
        cellsZ = MAX_CELLS_Z;
    // ---- Corner lattice: the only field access in the whole contact solve ----
    const lx = cellsX + 1;
    const ly = cellsY + 1;
    const lz = cellsZ + 1;
    const strideY = lx;
    const strideZ = lx * ly;
    for (let k = 0; k < lz; k++) {
        for (let j = 0; j < ly; j++) {
            let idx = j * strideY + k * strideZ;
            for (let i = 0; i < lx; i++) {
                lattice[idx++] = field.cornerSolidW(g0x + i, g0y + j, g0z + k);
            }
        }
    }
    // ---- One piece per mixed cell ----
    for (let k = 0; k < cellsZ; k++) {
        for (let j = 0; j < cellsY; j++) {
            for (let i = 0; i < cellsX; i++) {
                const base = i + j * strideY + k * strideZ;
                cw[0] = lattice[base];
                cw[1] = lattice[base + 1];
                cw[2] = lattice[base + strideY];
                cw[3] = lattice[base + strideY + 1];
                cw[4] = lattice[base + strideZ];
                cw[5] = lattice[base + strideZ + 1];
                cw[6] = lattice[base + strideY + strideZ];
                cw[7] = lattice[base + strideY + strideZ + 1];
                let air = 0;
                for (let g = 0; g < 8; g++)
                    if (cw[g] < 0)
                        air |= 1 << g;
                if (air === 0 || air === 0xff)
                    continue; // wholly solid or wholly air — no surface
                // SurfaceNets vertex: mean of the crossings on sign-changing edges, in cell-local
                // units. Same arithmetic as the mesher, so the piece sits on the rendered surface. The
                // running min/max over those same crossings is the piece's extent.
                let fx = 0, fy = 0, fz = 0;
                let n = 0;
                let loX = 1, loY = 1, loZ = 1;
                let hiX = 0, hiY = 0, hiZ = 0;
                for (let e = 0; e < 24; e += 2) {
                    const e0 = CUBE_EDGES[e];
                    const e1 = CUBE_EDGES[e + 1];
                    const w0 = cw[e0];
                    const w1 = cw[e1];
                    if ((w0 < 0) === (w1 < 0))
                        continue;
                    const t = w0 / (w0 - w1); // exact zero of the linear edge; denominator cannot vanish
                    n++;
                    // Along the edge's own axis the coordinate is the crossing; on the other two it is
                    // the shared corner value.
                    const ax = e0 & 1, bx2 = e1 & 1;
                    const cx2 = ax !== bx2 ? (ax ? 1 - t : t) : (ax ? 1 : 0);
                    const ay = e0 & 2, by2 = e1 & 2;
                    const cy2 = ay !== by2 ? (ay ? 1 - t : t) : (ay ? 1 : 0);
                    const az = e0 & 4, bz2 = e1 & 4;
                    const cz2 = az !== bz2 ? (az ? 1 - t : t) : (az ? 1 : 0);
                    fx += cx2;
                    fy += cy2;
                    fz += cz2;
                    if (cx2 < loX)
                        loX = cx2;
                    if (cx2 > hiX)
                        hiX = cx2;
                    if (cy2 < loY)
                        loY = cy2;
                    if (cy2 > hiY)
                        hiY = cy2;
                    if (cz2 < loZ)
                        loZ = cz2;
                    if (cz2 > hiZ)
                        hiZ = cz2;
                }
                if (n === 0)
                    continue;
                const inv = 1 / n;
                fx *= inv;
                fy *= inv;
                fz *= inv;
                // Analytic trilinear gradient at the vertex (VoxelField.gradSolid's arithmetic, on the
                // cached corners). Grid units — only the direction survives normalization.
                const x00 = cw[0] + (cw[1] - cw[0]) * fx;
                const x10 = cw[2] + (cw[3] - cw[2]) * fx;
                const x01 = cw[4] + (cw[5] - cw[4]) * fx;
                const x11 = cw[6] + (cw[7] - cw[6]) * fx;
                const a0 = (cw[1] - cw[0]) + ((cw[3] - cw[2]) - (cw[1] - cw[0])) * fy;
                const a1 = (cw[5] - cw[4]) + ((cw[7] - cw[6]) - (cw[5] - cw[4])) * fy;
                const gx = a0 + (a1 - a0) * fz;
                const gy = (x10 - x00) + ((x11 - x01) - (x10 - x00)) * fz;
                const gz = (x01 - x00) + ((x11 - x10) - (x01 - x00)) * fy;
                const gm = Math.sqrt(gx * gx + gy * gy + gz * gz);
                if (gm < 1e-9)
                    continue; // no orientation information (cannot happen for a mixed cell)
                const p = out.count;
                if (p >= MAX_PIECES)
                    return;
                const ox = (g0x + i) * VOXEL_SCALE;
                const oy = (g0y + j) * VOXEL_SCALE;
                const oz = (g0z + k) * VOXEL_SCALE;
                out.vx[p] = ox + fx * VOXEL_SCALE;
                out.vy[p] = oy + fy * VOXEL_SCALE;
                out.vz[p] = oz + fz * VOXEL_SCALE;
                out.nx[p] = -gx / gm;
                out.ny[p] = -gy / gm;
                out.nz[p] = -gz / gm;
                out.loX[p] = ox + loX * VOXEL_SCALE;
                out.loY[p] = oy + loY * VOXEL_SCALE;
                out.loZ[p] = oz + loZ * VOXEL_SCALE;
                out.hiX[p] = ox + hiX * VOXEL_SCALE;
                out.hiY[p] = oy + hiY * VOXEL_SCALE;
                out.hiZ[p] = oz + hiZ * VOXEL_SCALE;
                out.count = p + 1;
            }
        }
    }
}
//# sourceMappingURL=surfacePieces.js.map